/**
 * @file resolveAttachmentChangedAt.ts
 * @description The moment one attachment last changed, as the attachments
 * panel's file row both prints it and sorts on it. Consumer: `AttachmentList`.
 *
 * The version's own `meta` is what a new version writes:
 * `commitAttachmentVersion` stamps the artifact entry's `meta` only when it
 * creates that entry and never again, so a file revised after upload would
 * otherwise keep its upload date and its upload position. The entry's `meta`
 * is the last resort, for a file holding no version at all — `renameFile` is
 * its only other writer, so reading it first would float a renamed file above
 * genuinely newer ones.
 *
 * The caller passes the version its row lists, so the date shown and the date
 * sorted on are the same version's, whichever version the row resolved to.
 */

import { Timestamp } from 'firebase/firestore';

import type { ArtifactAttachments, AttachmentVersion } from '#types/System.types';

export const resolveAttachmentChangedAt = (
    version: AttachmentVersion | undefined,
    attachment: ArtifactAttachments[string] | undefined,
): Timestamp | null =>
    [
        version?.meta?.lastModified,
        version?.meta?.created,
        attachment?.meta?.lastModified,
        attachment?.meta?.created,
    ].find((candidate): candidate is Timestamp => candidate instanceof Timestamp) ?? null;
